import React, { useCallback, useEffect, useState } from 'react';

// Launch tab — push a campaign's members into an outreach tool (lemlist /
// SourceWhale), guarded against double-sending, with a live send queue.
// The backend (POST /campaigns/:id/launch) only pushes not-yet-launched
// members; status is tracked on campaign_cards.

type Channel = 'lemlist' | 'sourcewhale';

interface LaunchMember {
  entity_type: 'candidate' | 'contact';
  entity_id: number;
  name: string;
  launched_at: string | null;
  launch_channel: string | null;
  launch_external_name: string | null;
  launch_status: 'sent' | 'skipped' | 'failed' | null;
  launch_error: string | null;
}

interface LaunchStatus {
  total: number;
  launched: number;
  pending: number;
  members: LaunchMember[];
}

const CHANNEL_LABEL: Record<Channel, string> = { lemlist: 'lemlist', sourcewhale: 'SourceWhale' };

// Optional fields pushed as lemlist {{variables}}. Headline & skills are always
// sent, so they're not listed here.
const FIELD_OPTIONS: { key: string; label: string }[] = [
  { key: 'summary', label: 'Profile summary' },
  { key: 'current_position_description', label: 'Current role description' },
  { key: 'previous_company_1', label: 'Previous company 1' },
  { key: 'previous_company_2', label: 'Previous company 2' },
  { key: 'previous_company_3', label: 'Previous company 3' },
  { key: 'previous_position_1', label: 'Previous title 1' },
  { key: 'previous_position_2', label: 'Previous title 2' },
  { key: 'previous_position_3', label: 'Previous title 3' },
  { key: 'previous_position_1_description', label: 'Previous role 1 description' },
  { key: 'previous_position_2_description', label: 'Previous role 2 description' },
  { key: 'previous_position_3_description', label: 'Previous role 3 description' },
];

const CampaignLaunch: React.FC<{ campaignId: string; token: string | null }> = ({ campaignId, token }) => {
  const auth = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const [connected, setConnected] = useState<Record<Channel, boolean>>({ lemlist: false, sourcewhale: false });
  const [channel, setChannel] = useState<Channel | null>(null);
  const [destinations, setDestinations] = useState<{ id: string; name: string }[]>([]);
  const [loadingDest, setLoadingDest] = useState(false);
  const [destId, setDestId] = useState('');
  const [status, setStatus] = useState<LaunchStatus | null>(null);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  // Optional profile fields to push as lemlist variables (beyond the core
  // name/company/title/email/phone/LinkedIn/headline/skills always sent).
  const [selectedFields, setSelectedFields] = useState<Set<string>>(new Set(['summary', 'current_position_description']));
  const [showFields, setShowFields] = useState(true);
  // Custom + campaign columns: always pushed as {{variables}} — listed so the
  // user knows the exact variable names to use in their lemlist template.
  const [customVars, setCustomVars] = useState<{ key: string; label: string }[]>([]);
  const sanitizeVar = (k: string) => String(k || '').replace(/[^a-zA-Z0-9_]/g, '_').replace(/^_+|_+$/g, '') || 'field';

  const loadStatus = useCallback(async () => {
    const res = await fetch(`/api/campaigns/${campaignId}/launch-status`, { headers: auth });
    if (res.ok) setStatus(await res.json());
  }, [campaignId, token]);

  useEffect(() => {
    (async () => {
      const [l, s] = await Promise.all([
        fetch('/api/lemlist/status', { headers: auth }).then(r => r.ok ? r.json() : { connected: false }).catch(() => ({ connected: false })),
        fetch('/api/sourcewhale/status', { headers: auth }).then(r => r.ok ? r.json() : { connected: false }).catch(() => ({ connected: false })),
      ]);
      const conn = { lemlist: !!l.connected, sourcewhale: !!s.connected };
      setConnected(conn);
      // Default to the first connected channel.
      setChannel(prev => prev || (conn.lemlist ? 'lemlist' : conn.sourcewhale ? 'sourcewhale' : null));

      // Gather the custom columns that ride along automatically: this
      // campaign's own columns + the project's custom/AI columns.
      try {
        const vars: { key: string; label: string }[] = [];
        const campCols = await fetch(`/api/campaigns/${campaignId}/columns`, { headers: auth }).then(r => r.ok ? r.json() : []);
        (Array.isArray(campCols) ? campCols : []).forEach((c: any) => vars.push({ key: c.column_key, label: c.column_label }));
        const camp = await fetch(`/api/campaigns/${campaignId}`, { headers: auth }).then(r => r.ok ? r.json() : null);
        if (camp?.project_id) {
          const projCols = await fetch(`/api/custom-columns/${camp.project_id}`, { headers: auth }).then(r => r.ok ? r.json() : []);
          (Array.isArray(projCols) ? projCols : []).forEach((c: any) => {
            if (!vars.some(v => v.key === c.column_key)) vars.push({ key: c.column_key, label: c.column_label });
          });
        }
        setCustomVars(vars);
      } catch { /* informational only */ }
    })();
    loadStatus();
  }, [campaignId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load the chosen tool's destination campaigns.
  useEffect(() => {
    if (!channel || !connected[channel]) { setDestinations([]); return; }
    setLoadingDest(true);
    setDestId('');
    fetch(`/api/${channel}/campaigns`, { headers: auth })
      .then(r => r.ok ? r.json() : [])
      .then((list: any[]) => setDestinations(Array.isArray(list) ? list.map(c => ({ id: String(c.id), name: c.name || 'Untitled campaign' })) : []))
      .catch(() => setDestinations([]))
      .finally(() => setLoadingDest(false));
  }, [channel]); // eslint-disable-line react-hooks/exhaustive-deps

  const launch = async () => {
    if (!channel || !destId) return;
    setSending(true);
    setResult(null);
    try {
      const dest = destinations.find(d => d.id === destId);
      const res = await fetch(`/api/campaigns/${campaignId}/launch`, {
        method: 'POST',
        headers: auth,
        body: JSON.stringify({ channel, externalCampaignId: destId, externalCampaignName: dest?.name, fields: Array.from(selectedFields) }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Launch failed');
      setResult(data.message || 'Launched');
      await loadStatus();
    } catch (e: any) {
      setResult(e.message);
    } finally {
      setSending(false);
    }
  };

  const anyConnected = connected.lemlist || connected.sourcewhale;
  const pending = status?.pending ?? 0;

  const statusBadge = (m: LaunchMember) => {
    if (m.launch_status === 'sent') return <span className="px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700">Launched</span>;
    if (m.launch_status === 'failed') return <span className="px-2 py-0.5 rounded-full text-xs bg-red-100 text-red-700" title={m.launch_error || ''}>Failed</span>;
    if (m.launch_status === 'skipped') return <span className="px-2 py-0.5 rounded-full text-xs bg-amber-100 text-amber-700" title={m.launch_error || ''}>Skipped</span>;
    return <span className="px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-500">Pending</span>;
  };

  return (
    <div className="space-y-5">
      {/* Summary */}
      <div className="flex items-center gap-6 text-sm">
        <div><span className="text-2xl font-semibold text-gray-800">{status?.total ?? '—'}</span> <span className="text-gray-500">members</span></div>
        <div><span className="text-2xl font-semibold text-green-600">{status?.launched ?? '—'}</span> <span className="text-gray-500">launched</span></div>
        <div><span className="text-2xl font-semibold text-gray-800">{pending}</span> <span className="text-gray-500">pending</span></div>
      </div>

      {/* Launch controls */}
      <div className="card p-5 space-y-4">
        {!anyConnected ? (
          <p className="text-sm text-gray-500">
            No outreach tool connected. Add a lemlist or SourceWhale API key in <strong>Settings → Integrations</strong> to launch this campaign.
          </p>
        ) : (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Channel</label>
              <div className="flex gap-2">
                {(['lemlist', 'sourcewhale'] as Channel[]).map(ch => (
                  <button
                    key={ch}
                    onClick={() => connected[ch] && setChannel(ch)}
                    disabled={!connected[ch]}
                    className={`px-4 py-2 rounded-lg text-sm font-medium border ${
                      channel === ch ? 'border-blue-600 bg-blue-50 text-blue-700'
                      : connected[ch] ? 'border-gray-200 text-gray-700 hover:bg-gray-50'
                      : 'border-gray-100 text-gray-300 cursor-not-allowed'
                    }`}
                  >
                    {CHANNEL_LABEL[ch]}{!connected[ch] && ' (not connected)'}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Destination campaign</label>
              <select value={destId} onChange={e => setDestId(e.target.value)} className="input text-sm max-w-md" disabled={loadingDest}>
                <option value="">{loadingDest ? 'Loading…' : `Select a ${channel ? CHANNEL_LABEL[channel] : ''} campaign…`}</option>
                {destinations.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>

            {/* Optional fields → lemlist variables */}
            <div>
              <button
                onClick={() => setShowFields(v => !v)}
                className="text-sm text-gray-600 hover:text-gray-800"
              >
                {showFields ? '▾' : '▸'} Fields to send as variables
                <span className="text-gray-400 ml-1">
                  (always: name, company, title, email, phone, LinkedIn, headline, skills{selectedFields.size ? ` + ${selectedFields.size} extra` : ''})
                </span>
              </button>
              {showFields && (
                <div className="mt-2 grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1 max-w-2xl">
                  {FIELD_OPTIONS.map(f => (
                    <label key={f.key} className="flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={selectedFields.has(f.key)}
                        onChange={e => setSelectedFields(prev => {
                          const n = new Set(prev);
                          if (e.target.checked) n.add(f.key); else n.delete(f.key);
                          return n;
                        })}
                      />
                      {f.label}
                    </label>
                  ))}
                </div>
              )}
              {showFields && customVars.length > 0 && (
                <div className="mt-3">
                  <p className="text-xs font-medium text-gray-500 mb-1.5">
                    Your custom &amp; AI columns are always sent too. Use these variables in your lemlist template:
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {customVars.map(v => (
                      <span key={v.key} className="inline-flex items-center gap-1.5 bg-violet-50 border border-violet-200 text-violet-700 rounded-md px-2 py-1 text-xs font-mono" title={v.label}>
                        {'{{'}{sanitizeVar(v.key)}{'}}'}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {showFields && customVars.length === 0 && (
                <p className="mt-1 text-xs text-gray-400">Custom &amp; AI columns you've created are always sent too, keyed by their column name.</p>
              )}
            </div>

            <div className="flex items-center gap-3 pt-1">
              <button
                onClick={launch}
                disabled={sending || !destId || pending === 0}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {sending ? 'Launching…' : `Launch ${pending} pending member${pending === 1 ? '' : 's'}`}
              </button>
              {pending === 0 && status && status.total > 0 && (
                <span className="text-xs text-gray-400">All members already launched.</span>
              )}
              {result && <span className="text-sm text-gray-600">{result}</span>}
            </div>
            <p className="text-xs text-gray-400">
              Only members that haven’t been launched yet are pushed. Contacts with no email are still sent for LinkedIn-only sequences (keyed by LinkedIn URL).
            </p>
          </>
        )}
      </div>

      {/* Send queue */}
      {status && status.members.length > 0 && (
        <div className="card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left font-medium px-4 py-2">Member</th>
                <th className="text-left font-medium px-4 py-2">Type</th>
                <th className="text-left font-medium px-4 py-2">Status</th>
                <th className="text-left font-medium px-4 py-2">Destination</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {status.members.map(m => (
                <tr key={`${m.entity_type}-${m.entity_id}`}>
                  <td className="px-4 py-2 text-gray-800">{m.name}</td>
                  <td className="px-4 py-2 text-gray-500 capitalize">{m.entity_type}</td>
                  <td className="px-4 py-2">{statusBadge(m)}</td>
                  <td className="px-4 py-2 text-gray-500">{m.launch_external_name || (m.launch_channel ? CHANNEL_LABEL[m.launch_channel as Channel] : '—')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default CampaignLaunch;
